<template>
    <div ref="variantSelectorRef" class="mb-5 max-w-[450px] md:max-w-none lg:max-w-[450px]">
        <div class="mb-4 last:mb-0!" v-for="variant in optionsObject">
            <div v-if="variant.translated.name !== 'Kleur'">
                <p
                    v-if="variant.translated.name !== 'Maat'"
                    class="te mb-6 leading-none font-bold lg:mb-2 lg:leading-normal">
                    {{ variant.translated.name }}
                </p>

                <div
                    v-if="variant.translated.name === 'Maat'"
                    class="mb-3 flex max-w-[450px] md:max-w-none lg:max-w-[450px] flex-wrap items-center justify-between gap-x-3 gap-y-2 text-sm font-bold">
                    <span class="min-w-0">{{ snippets['headless.pdp.chooseYourSize'] ?? 'Kies je maat' }}</span>
                    <FasletCustomButton
                        v-if="hasFasletPdpIntegration"
                        variant="inline"
                        :ref="bindFasletCustomButtonRef" />
                </div>
                <div
                    class="flex flex-wrap gap-2"
                    :class="{
                        'rounded-sm ring-2 ring-red-600 ring-offset-2':
                            showVariantHint && variant.translated.name !== 'Kleur',
                    }">
                    <button
                        v-for="option in variant.options"
                        :key="option.id"
                        type="button"
                        class="relative size-[50px] overflow-hidden rounded-sm bg-green-400 p-[4px] text-sm font-bold break-all tabular-nums duration-200 hover:bg-green-500"
                        :class="{
                            'pointer-events-none bg-green-600!': isSelected(option),
                            'pointer-events-none bg-green-200! text-green-500 opacity-40': option.stock === 0,
                        }"
                        :aria-pressed="isSelected(option)"
                        @click="selectOption(option)">
                        {{ option.translated.name }}
                    </button>
                </div>
            </div>
        </div>
        <p v-if="showVariantHint" id="pdp-variant-hint" role="alert" class="mt-3 text-sm leading-snug text-red-700">
            {{ snippets['headless.pdp.selectVariantMessage'] }}
        </p>
    </div>
    <div>
        <template v-if="errors && variantId">
            <div class="mb-5 flex items-center" v-if="errors['product-out-of-stock' + variantId]">
                <InformationCircleIcon class="mr-3 size-5 stroke-2" />
                {{ snippets['headless.pdp.productOutOfStock'] }}
            </div>
            <div class="mb-5 flex items-center" v-else-if="errors[variantId]">
                <InformationCircleIcon class="mr-3 size-5 stroke-2" />
                {{ snippets['headless.cart.productOutOfStockErrorMessage'] }}
            </div>
            <div class="mb-5 flex items-center" v-else-if="errors['product-stock-reached' + variantId]">
                <InformationCircleIcon class="mr-3 size-5 stroke-2" />
                {{ snippets['headless.pdp.productOutOfStock'] }}
            </div>
        </template>

        <!-- Observed by StickyAddToCart: when this block is in view, the fixed bar hides -->

        <div class="mb-2 w-full max-w-[450px] md:max-w-none lg:max-w-[450px]">
            <ShopwareFasletWidget :product="product" :variants="variants" :add-to-cart="handleAddToCart" />
        </div>

        <div ref="primaryCtaAnchor" class="primary-cta-anchor">
            <button
                v-if="isSoldOut"
                type="button"
                class="btn btn-yellow pointer-events-none mb-2 flex! w-full max-w-[450px] md:max-w-none lg:max-w-[450px] cursor-not-allowed justify-center opacity-40">
                <span>{{ snippets['headless.pdp.soldOut'] }}</span>
            </button>
            <button
                v-else
                type="button"
                class="btn btn-yellow mb-2 flex! w-full max-w-[450px] md:max-w-none lg:max-w-[450px] justify-center"
                :class="{
                    'pointer-events-none cursor-not-allowed opacity-40 hover:bg-black!':
                        isAddingToCart || isVariantOutOfStock,
                }"
                :disabled="isAddingToCart || isVariantOutOfStock"
                :aria-busy="isAddingToCart"
                :aria-disabled="isAddingToCart || isVariantOutOfStock"
                :aria-describedby="showVariantHint ? 'pdp-variant-hint' : undefined"
                @click="handleAddToCartClick">
                <NuxtImg
                    :alt="snippets['headless.icon.shoppingbag']"
                    class="mr-3 size-4"
                    src="/icons/shopping-bag_black.svg" />
                <span>{{ snippets['headless.pdp.inCartButton'] }}</span>
            </button>
        </div>

        <button
            @click="openModal"
            v-if="layout !== 'looks' && siteConfig[0]?.showReservation"
            class="btn w-full max-w-[450px] md:max-w-none lg:max-w-[450px] bg-green-200 text-center hover:bg-green-500 lg:px-40!"
            :class="[
                {
                    'pointer-events-none cursor-not-allowed opacity-40 hover:!bg-black': !canMakeReservation,
                },
            ]">
            {{ snippets['headless.pdp.reserveButton'] }}
        </button>

        <ReservationRequestModal
            v-if="showModal"
            @close="showModal = false"
            :product="product"
            :variants="variants"
            role="alertdialog" />
    </div>
</template>
<script setup lang="ts">
    import { InformationCircleIcon } from '@heroicons/vue/24/outline';
    import FasletCustomButton from './FasletCustomButton.vue';
    import ShopwareFasletWidget from '~/components/shopware/product/detail/ShopwareFasletWidget.vue';

    const props = defineProps({
        product: Object,
        variants: Object,
        layout: String,
    });

    /** Must run synchronously during setup (before any `await`) so inject resolves on SSR/prod builds. */
    const fasletPdpAddToCartHandler = useFasletPdpAddToCartHandler();

    const showModal = ref(false);
    const { addProduct, refreshCart, removeItem } = useCart();
    const addedToCart = ref(false);
    const showVariantHint = ref(false);
    const canAddToCart = ref(false);
    /** Prevents re-entry during in-flight `addProduct` calls so click-spamming can't double-emit GTM events. */
    const isAddingToCart = ref(false);
    /** Set once Shopware reports the currently selected variant is out of stock; locks the CTA so another `item_out_of_stock` push cannot fire until a different variant is chosen. */
    const isVariantOutOfStock = ref(false);
    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;
    const siteConfig = await useSiteSettings();
    const errors = ref();
    const variantId = ref();
    const { trackProductClick, trackAddToCart, trackOutOfStockMessage } = useGTMEcommerce();
    const optionsObject = ref(getOptions());
    const cartSidebarStore = useCartSidebarStore();
    const { invokeCartSidebar } = cartSidebarStore;
    const { canMakeReservation, isSoldOut } = useProductState(props.variants);

    /** Injected ref from PDP `provideFasletCustomButtonTarget()` — not the inner API value (starts null until the child mounts). */
    const fasletCustomButtonTargetRef = useFasletCustomButtonTarget();
    const hasFasletPdpIntegration = fasletCustomButtonTargetRef != null;

    function bindFasletCustomButtonRef(el: unknown) {
        if (!fasletCustomButtonTargetRef) {
            return;
        }
        fasletCustomButtonTargetRef.value = (el as InstanceType<typeof FasletCustomButton>) ?? null;
    }

    const openModal = () => {
        showModal.value = true;
        document.body.style.overflow = 'hidden';
    };

    function triggerViewItemEvent() {
        trackProductClick(props.product, 'pdp', 'Product Detail Page', 0);
    }

    function triggerAtcEvent() {
        const variant = selectedOptions.value[0]?.translated?.name || '';
        trackAddToCart(props.product, 1, variant);
    }

    function syncSelectionFromChildProduct(child: Record<string, any>) {
        const ids = new Set<string>(child.optionIds ?? []);
        if (!Array.isArray(props.variants)) {
            return;
        }
        const nextSelected: any[] = [];
        for (const group of props.variants) {
            const opt = (group.options ?? []).find((o: { id: string }) => ids.has(o.id));
            if (opt) {
                nextSelected.push(opt);
            }
        }
        selectedOptions.value = nextSelected;
        canAddToCart.value = nextSelected.length > 0;
    }

    function sizeLabelForTracking(child: Record<string, any> | undefined): string {
        if (!child?.optionIds?.length || !Array.isArray(props.variants)) {
            return '';
        }
        const idSet = new Set<string>(child.optionIds);
        for (const group of props.variants) {
            if (group.translated?.name === 'Maat') {
                const opt = (group.options ?? []).find((o: { id: string }) => idSet.has(o.id));
                if (opt) {
                    return opt.translated?.name ?? opt.name ?? '';
                }
            }
        }
        return '';
    }

    async function addProductAndRefresh(): Promise<boolean> {
        if (isAddingToCart.value || isVariantOutOfStock.value) {
            return false;
        }
        isAddingToCart.value = true;
        try {
            const id = variantId.value ?? props.product.id;
            const result = await addProduct({ id, quantity: 1 });
            if (result.errors) {
                errors.value = result.errors;
                if (
                    errors.value['product-out-of-stock' + variantId.value] ||
                    errors.value['product-stock-reached' + variantId.value]
                ) {
                    trackOutOfStockMessage(props.product, 'pdp', 'Product Detail Page', 0);
                    isVariantOutOfStock.value = true;
                    return false;
                }
                if (errors.value[variantId.value]) {
                    trackOutOfStockMessage(props.product, 'pdp', 'Product Detail Page', 0);
                    isVariantOutOfStock.value = true;
                    await removeItem({ id: id });
                    return false;
                }
            }

            await refreshCart();
            triggerAtcEvent();
            invokeCartSidebar();
            addedToCart.value = true;
            clearVariantSelection();
            return true;
        } finally {
            isAddingToCart.value = false;
        }
    }

    /**
     * Called by Faslet (`withAddToCart`) with the Shopware variant (child) product id.
     */
    async function addToCart(fasletVariantId: string) {
        if (!fasletVariantId) {
            return;
        }
        if (isAddingToCart.value) {
            return;
        }
        isAddingToCart.value = true;
        try {
            variantId.value = fasletVariantId;
            errors.value = undefined;
            isVariantOutOfStock.value = false;

            const child = props.product?.children?.find((c: { id: string }) => c.id === fasletVariantId);
            if (child) {
                syncSelectionFromChildProduct(child);
            }

            const result = await addProduct({ id: fasletVariantId, quantity: 1 });
            if (result.errors) {
                errors.value = result.errors;
                if (
                    errors.value['product-out-of-stock' + fasletVariantId] ||
                    errors.value['product-stock-reached' + fasletVariantId]
                ) {
                    trackOutOfStockMessage(props.product, 'pdp', 'Product Detail Page', 0);
                    isVariantOutOfStock.value = true;
                    return;
                }
                if (errors.value[fasletVariantId]) {
                    trackOutOfStockMessage(props.product, 'pdp', 'Product Detail Page', 0);
                    isVariantOutOfStock.value = true;
                    await removeItem({ id: fasletVariantId });
                    return;
                }
            }

            await refreshCart();
            const variantLabel = sizeLabelForTracking(child) || selectedOptions.value[0]?.translated?.name || '';
            trackAddToCart(props.product, 1, variantLabel);
            invokeCartSidebar();
            addedToCart.value = true;
            clearVariantSelection();
        } finally {
            isAddingToCart.value = false;
        }
    }

    const primaryCtaAnchor = ref<HTMLElement | null>(null);

    /** Variant groups shown in the sticky bar (everything except colour). */
    const stickyVariantGroups = computed(() =>
        optionsObject.value.filter(
            (variant: { translated?: { name?: string } }) => variant.translated?.name !== 'Kleur',
        ),
    );

    const variantStockErrorMessage = computed((): string | null => {
        if (!errors.value || !variantId.value) {
            return null;
        }
        if (errors.value['product-out-of-stock' + variantId.value]) {
            return snippets['headless.pdp.productOutOfStock'] ?? null;
        }
        if (errors.value[variantId.value]) {
            return snippets['headless.cart.productOutOfStockErrorMessage'] ?? null;
        }
        if (errors.value['product-stock-reached' + variantId.value]) {
            return snippets['headless.pdp.productOutOfStock'] ?? null;
        }
        return null;
    });

    defineExpose({
        addToCart,
        primaryCtaAnchor,
        addProductAndRefresh,
        clearVariantSelection,
        selectionButtonClick,
        selectOption,
        isSelected,
        stickyVariantGroups,
        canAddToCart,
        isSoldOut,
        isAddingToCart,
        isVariantOutOfStock,
        variantStockErrorMessage,
    });

    if (fasletPdpAddToCartHandler) {
        fasletPdpAddToCartHandler.value = addToCart;
        onUnmounted(() => {
            if (fasletPdpAddToCartHandler.value === addToCart) {
                fasletPdpAddToCartHandler.value = null;
            }
        });
    }

    if (props.variants.length === 0) {
        canAddToCart.value = true;
    }

    const selectedOptions = ref([]);
    const variantSelectorRef = ref<HTMLElement | null>(null);

    function isVariantSelectorInView(): boolean {
        const el = variantSelectorRef.value;
        if (!el || !import.meta.client) {
            return true;
        }
        const rect = el.getBoundingClientRect();
        const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        return visibleHeight > 0 && visibleHeight >= Math.min(rect.height, 80) * 0.25;
    }

    function scrollToVariantSelector() {
        variantSelectorRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function promptVariantSelection() {
        showVariantHint.value = true;
        nextTick(() => {
            if (!isVariantSelectorInView()) {
                scrollToVariantSelector();
            }
        });
    }

    function handleAddToCartClick() {
        if (canAddToCart.value) {
            showVariantHint.value = false;
            void addProductAndRefresh();
            return;
        }
        if (props.variants?.length > 0) {
            promptVariantSelection();
        }
    }

    async function selectOption(option) {
        showVariantHint.value = false;

        const existingOption = selectedOptions.value.find(opt => opt.groupId === option.groupId);
        if (existingOption) {
            const index = selectedOptions.value.indexOf(existingOption);
            selectedOptions.value.splice(index, 1, option);
        } else {
            selectedOptions.value.push(option);
        }

        if (selectedOptions.value.length === props.variants.length) {
            if (props.variants.length !== 0) {
                variantId.value = await useFindVariant(props.product.id, selectedOptions.value);
            }

            isVariantOutOfStock.value = false;
            errors.value = undefined;
            canAddToCart.value = true;
            triggerViewItemEvent();
        }
    }

    function isSelected(option) {
        return selectedOptions.value.some(opt => opt.id === option.id);
    }

    function clearVariantSelection() {
        selectedOptions.value = [];
        variantId.value = undefined;
        errors.value = undefined;
        isVariantOutOfStock.value = false;
        canAddToCart.value = props.variants.length === 0;
        showVariantHint.value = false;
    }

    function getOptions() {
        const optionsObject = ref(props.variants);

        // sort the variants by the option position
        optionsObject.value.forEach(variant => {
            variant.options.sort((a, b) => a.position - b.position);
        });

        const children = ref(props?.product?.children);

        optionsObject.value.forEach(variant => {
            variant.options.forEach(option => {
                children.value.forEach(product => {
                    if (product.optionIds.includes(option.id)) {
                        option.stock = product.stock;
                    }
                });
            });
        });

        return optionsObject.value;
    }

    function selectionButtonClick(): void {
        promptVariantSelection();
    }
</script>
